import { Link, useLocation } from "react-router-dom";
import { ChevronDown, LogOut, User, Map, ArrowLeft, ArrowRight, Wand2, Images, Megaphone, Palette, BarChart3, Coins, Puzzle, GraduationCap, Shield } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const ANALYTICS_PLANS = new Set(["standard", "growth", "pro", "agency", "enterprise"]);

/** Everything a signed-in user can reach, grouped for the workspace map. */
export const accountSections = (user) => [
  {
    title: "Create",
    links: [
      { to: "/studio", label: "Studio", id: "studio", icon: Wand2, hint: "Make ads, images and voiceovers" },
      { to: "/gallery", label: "Gallery", id: "gallery", icon: Images, hint: "Everything you've generated" },
      { to: "/campaigns", label: "Campaigns", id: "campaigns", icon: Megaphone, hint: "Group ads by launch" },
      { to: "/brand-kit", label: "Brand Kit", id: "brand-kit", icon: Palette, hint: "Colours, fonts and voice" },
      ANALYTICS_PLANS.has(user?.package_id) && { to: "/analytics", label: "Analytics", id: "analytics", icon: BarChart3, hint: "What's converting" },
    ].filter(Boolean),
  },
  {
    title: "Account",
    links: [
      { to: "/credits", label: "Credits", id: "credits", icon: Coins, hint: "Balance, top-ups and history" },
      { to: "/addons", label: "Add-ons", id: "addons", icon: Puzzle, hint: "Extra seats and features" },
      { to: "/education", label: "Education", id: "education", icon: GraduationCap, hint: "Guides and playbooks" },
      { to: "/profile", label: "Profile & billing", id: "profile", icon: User, hint: "Plan, card, pause" },
      user?.is_admin && { to: "/admin", label: "Admin", id: "admin", icon: Shield, hint: "Metrics and users", admin: true },
    ].filter(Boolean),
  },
];

const currentLabel = (user, pathname) => {
  for (const s of accountSections(user)) {
    const hit = s.links.find((l) => pathname === l.to || pathname.startsWith(`${l.to}/`));
    if (hit) return hit.label;
  }
  return "My workspace";
};

const itemCls = "flex items-start gap-3 rounded-xl px-3 py-2.5 cursor-pointer focus:bg-white/[0.07] data-[highlighted]:bg-white/[0.07]";

/** The "map": one compact dropdown replaces the row of eight account links. */
export const WorkspaceMenu = ({ user }) => {
  const { pathname } = useLocation();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" data-testid="workspace-menu-trigger"
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold text-white bg-white/[0.07] hover:bg-white/[0.14] ring-1 ring-white/15 transition-colors whitespace-nowrap outline-none">
          <Map className="w-4 h-4 text-violet-300" />
          {currentLabel(user, pathname)}
          <ChevronDown className="w-3.5 h-3.5 text-white/60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={10} className="w-[560px] p-3 rounded-2xl bg-[#0b0820]/95 backdrop-blur-2xl border-white/10 text-white shadow-2xl shadow-purple-900/40" data-testid="workspace-menu">
        <div className="grid grid-cols-2 gap-2">
          {accountSections(user).map((section) => (
            <div key={section.title}>
              <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-white/40 px-3">{section.title}</DropdownMenuLabel>
              {section.links.map((l) => {
                const active = pathname === l.to || pathname.startsWith(`${l.to}/`);
                const Icon = l.icon;
                return (
                  <DropdownMenuItem key={l.id} asChild className={itemCls}>
                    <Link to={l.to} data-testid={`nav-${l.id}-link`}>
                      <span className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${active ? "bg-violet-600 text-white" : l.admin ? "bg-cyan-500/15 text-cyan-300" : "bg-white/[0.06] text-violet-300"}`}><Icon className="w-4 h-4" /></span>
                      <span className="min-w-0">
                        <span className={`block text-sm font-semibold ${active ? "text-white" : "text-white/90"}`}>{l.label}</span>
                        <span className="block text-xs text-white/45 truncate">{l.hint}</span>
                      </span>
                    </Link>
                  </DropdownMenuItem>
                );
              })}
            </div>
          ))}
        </div>
        <DropdownMenuSeparator className="bg-white/10 my-2" />
        <DropdownMenuItem asChild className={itemCls}>
          <Link to="/" data-testid="nav-back-to-site" className="text-white/70">
            <ArrowLeft className="w-4 h-4 mt-0.5" /><span className="text-sm">Back to the public site</span>
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/** Avatar menu: profile, credits and log out — the only account chrome shown on the public site. */
export const AccountMenu = ({ user, logout, inAccount }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button type="button" data-testid="nav-profile-link" aria-label="Account menu"
        className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 text-white/80 hover:text-white bg-white/[0.04] hover:bg-white/[0.1] ring-1 ring-white/10 transition-colors outline-none">
        <span className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center text-xs font-bold text-white">
          {(user?.name || user?.email || "?").trim().charAt(0).toUpperCase()}
        </span>
        <span className="text-sm hidden md:block max-w-[140px] truncate">{user?.name || "Account"}</span>
        <ChevronDown className="w-3.5 h-3.5 text-white/50" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" sideOffset={10} className="w-60 p-2 rounded-2xl bg-[#0b0820]/95 backdrop-blur-2xl border-white/10 text-white shadow-2xl shadow-purple-900/40" data-testid="account-menu">
      <DropdownMenuLabel className="px-3 py-2">
        <span className="block text-sm font-semibold truncate">{user?.name}</span>
        <span className="block text-xs text-white/45 truncate">{user?.email}</span>
      </DropdownMenuLabel>
      <DropdownMenuSeparator className="bg-white/10" />
      {!inAccount && (
        <DropdownMenuItem asChild className={itemCls}>
          <Link to="/studio" data-testid="nav-open-account"><ArrowRight className="w-4 h-4 mt-0.5 text-violet-300" /><span className="text-sm font-semibold">Open my account</span></Link>
        </DropdownMenuItem>
      )}
      <DropdownMenuItem asChild className={itemCls}>
        <Link to="/profile" data-testid="account-menu-profile"><User className="w-4 h-4 mt-0.5 text-violet-300" /><span className="text-sm">Profile & billing</span></Link>
      </DropdownMenuItem>
      <DropdownMenuItem asChild className={itemCls}>
        <Link to="/credits" data-testid="account-menu-credits"><Coins className="w-4 h-4 mt-0.5 text-violet-300" /><span className="text-sm">Credits</span></Link>
      </DropdownMenuItem>
      <DropdownMenuSeparator className="bg-white/10" />
      <DropdownMenuItem onSelect={logout} className={`${itemCls} text-red-200 data-[highlighted]:bg-red-500/10`} data-testid="logout-btn">
        <LogOut className="w-4 h-4 mt-0.5" /><span className="text-sm">Log out</span>
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);
